<template>
  <nav>
    <VTabsV2
      :tabs="DemandsV2Service.PAGE_TABS"
      base-url="/demands-page"
      @update="onTabUpdate"
    />

    <OrdersPageFilterComponent
      v-if="activeTab?.id === 'in-work'"
      :filters="localBuyerOrdersPageFilter"
      show-payment-options
      show-delivery-options
      @[BuyerOrdersPageFilter.Events.Updated]="onOrdersFiltersUpdated"
    />
    <!--    последний элемент прибивается вправо -->
    <DemandsV2PageFilterComponent
      v-else
      :filters="localDemandsV2PageFilter"
      @[DemandsV2PageFilter.Events.Updated]="onDemandsV2FiltersUpdated"
    />
  </nav>


  <DemandsV2Registry
    v-if="isDemandTabActive"
    :key="activeTab.id"
    :params="demandParams"
    :hidden-columns="demandHiddenColumns"
    :show-delivery-date-indication="showDeliveryDateIndication"
  />
</template>

<script lang="ts">

import DemandsV2Registry         from '@/domains/DemandsV2/components/DemandV2Registry/DemandV2Registry.vue';
import OrderPositionsRegistry    from '@/domains/Orders/components/OrderPositionsRegistry/OrderPositionsRegistry.vue';
import OrdersPageFilterComponent from '@/domains/Orders/components/OrdersPage/OrdersPageFilter/OrdersPageFilterComponent.vue';

import BuyerOrdersPageFilter     from '@/domains/Orders/components/OrdersPage/OrdersPageFilter/OrdersPageFilter';
import DemandsV2Service          from '@/domains/DemandsV2/DemandsV2Service';

import type TabElement           from '@/lib/components/VTabsV2/TabElement';
import DemandsV2PageFilterComponent
  from "@/domains/DemandsV2/components/DemandsV2Page/DemandsV2PageFilter/DemandsV2PageFilterComponent.vue";
import DemandsV2PageFilter
  from "@/domains/DemandsV2/components/DemandsV2Page/DemandsV2PageFilter/DemandsV2PageFilter";
import Person from "@/domains/Persons/Person";

/**
 * Флаги жизненного цикла потребности для бэкенда.
 */
type LifeCycleStage = {
  isOptionsExists?: boolean;
  isOptionsSelected?: boolean;
  isApproved?: boolean;
  isFinished?: boolean;
  responsible?: Person
};

/**
 * Конфигурация таба реестра потребностей: всё, что зависит от активной вкладки,
 * собрано в одном месте.
 *  - params:        фильтры жизненного цикла, уходят в загрузчик реестра;
 *  - hiddenColumns: чёрный список колонок (по умолчанию видно всё).
 *
 * Цветовой индикации даты в конфиге нет: она была включена на всех табах,
 * то есть реально гейтилась только принадлежностью к Namangan.
 */
type TabConfig = {
  params:        LifeCycleStage;
  hiddenColumns: string[];
};

/**
 * Табы реестра потребностей. На in-work показывается реестр позиций заказов,
 * его в этой таблице нет — рендер гейтится isDemandTabActive.
 * qtyOrdered/qtyLeft скрыты на всех табах (исторически не показывались нигде).
 */
const TAB_CONFIG: Record<string, TabConfig> = {
  'new-requests': {
    params:        { isOptionsExists: false, isOptionsSelected: false, isApproved: false, isFinished: false },
    hiddenColumns: ['seller', 'manufacturer', 'qtyOrdered', 'qtyLeft', 'price', 'options', 'isApproved', 'buyerProductionSite', 'responsible'],
  },
  'under-review': {
    params:        { isOptionsExists: true, isOptionsSelected: false, isApproved: false, isFinished: false },
    hiddenColumns: ['seller', 'manufacturer', 'qtyOrdered', 'qtyLeft', 'price', 'isApproved', 'buyerProductionSite', 'responsible'],
  },
  'awaiting-approval': {
    params:        { isOptionsExists: true, isOptionsSelected: true, isApproved: false, isFinished: false },
    hiddenColumns: ['qtyOrdered', 'qtyLeft', 'options', 'buyerProductionSite', 'responsible'],
  },
  'draft': {
    params:        { isOptionsExists: true, isOptionsSelected: true, isApproved: true, isFinished: false },
    hiddenColumns: ['qtyOrdered', 'qtyLeft', 'options', 'buyerProductionSite', 'responsible'],
  },
};

export default defineComponent({
  name: 'DemandsV2Page',

  components: {
    DemandsV2PageFilterComponent,
    OrderPositionsRegistry,
    DemandsV2Registry,
    OrdersPageFilterComponent,
  },

  setup() {
    return {
      BuyerOrdersPageFilter,
      DemandsV2PageFilter,
      DemandsV2Service,
    };
  },

  data() {
    return {
      /** Фильтр заказов вкладки in-work, восстанавливается из localStorage */
      localBuyerOrdersPageFilter: BuyerOrdersPageFilter.fromLocalStorage(),
      localDemandsV2PageFilter: DemandsV2PageFilter.fromLocalStorage(),

      /**
       * Активная вкладка — сообщает VTabsV2 (@update), страница роут
       * не парсит. Заполняется до mounted: VTabsV2 эмитит начальное
       * значение из своего created (immediate watch).
       */
      activeTab: null as TabElement | null,
    };
  },

  computed: {
    /** Активна одна из вкладок реестра потребностей */
    isDemandTabActive(): boolean {
      if (this.activeTab === null) return false;
      return this.activeTab.id in TAB_CONFIG;
    },

    /** Конфиг активного таба. null — активной вкладки ещё нет или таб не наш. */
    tabConfig(): TabConfig | null {
      if (this.activeTab === null) return null;
      return TAB_CONFIG[this.activeTab.id] ?? null;
    },

    /** Фильтры реестра по активной вкладке (флаги жизненного цикла). */
    demandParams(): LifeCycleStage {
      if (this.tabConfig === null) return {};

      return {
        ...this.localDemandsV2PageFilter.getRequestParams(),
        ...this.tabConfig.params,
      };
    },

    /** Скрытые колонки по активной вкладке (чёрный список, по умолчанию — ничего). */
    demandHiddenColumns(): string[] {
      return this.tabConfig?.hiddenColumns ?? [];
    },

    /** Цветовая индикация даты поставки — только для Namangan. */
    showDeliveryDateIndication(): boolean {
      if (this.tabConfig === null) return false;
      return this.$core.currentPartner.isNamangan;
    },
  },

  methods: {
    /** Активную вкладку сообщает VTabsV2 — из роута в router-mode */
    onTabUpdate(tab: TabElement | null): void {
      this.activeTab = tab;
    },

    /**
     * Применение фильтра заказов на вкладке in-work.
     * nextTick: ждём пока Vue обновит переменную, затем эмитим событие перезагрузки
     */
    async onOrdersFiltersUpdated(newPageFilter: BuyerOrdersPageFilter): Promise<void> {
      this.localBuyerOrdersPageFilter = newPageFilter;

      await this.$nextTick();
      this.$core.eventBus.OrdersUpdated.emit();
    },

    /**
     * Применение фильтра потребностей.
     */
    async onDemandsV2FiltersUpdated(newPageFilter: DemandsV2PageFilter): Promise<void> {
      this.localDemandsV2PageFilter = newPageFilter;
    },
  },
});
</script>
